import { useState } from 'react';
import { Link } from 'react-router-dom';
import { blogArticles } from '../blog/article-data';
import { reviews } from '../reviews/review-data';
import SEOHead from '../../components/SEOHead';

export default function HomePage() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Interior Decor Solutions',
    url: siteUrl,
    description: 'Discover handpicked home decor that transforms houses into homes. Honest product reviews, styling tips, and budget-friendly recommendations.',
    publisher: {
      '@type': 'Organization',
      name: 'Interior Decor Solutions',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`,
      },
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/blog?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    const formData = new URLSearchParams();
    formData.append('email', email);

    try {
      const response = await fetch('https://readdy.ai/api/form/d5dbhf1dn6dhfpiac24g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you for subscribing!');
        setEmail('');
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get the 3 most recent reviews (sorted by id descending - newest first)
  const featuredProducts = [...reviews]
    .sort((a, b) => b.id - a.id)
    .slice(0, 3);

  const blogPosts = blogArticles.sort((a, b) => {
    // Sort by date descending (newest first)
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return dateB - dateA;
  }).slice(0, 3);

  const roomCategories = [
    {
      id: 1,
      name: 'Living Room',
      icon: 'ri-sofa-line',
      image: 'https://public.readdy.ai/ai/img_res/78ef344eeed1d2344495a1ba5cc3c7e8.jpg',
      subcategories: ['Sofas & Sectionals', 'Coffee Tables', 'Wall Art', 'Lighting'],
      link: '/room-inspiration#living-room'
    },
    {
      id: 2,
      name: 'Bedroom',
      icon: 'ri-hotel-bed-line',
      image: 'https://readdy.ai/api/search-image?query=Serene%20minimalist%20bedroom%20interior%20with%20comfortable%20bed%2C%20soft%20bedding%2C%20neutral%20tones%2C%20clean%20lines%2C%20peaceful%20atmosphere%2C%20simple%20elegant%20decor%2C%20warm%20lighting%2C%20contemporary%20furniture%20design&width=400&height=300&seq=bedroom-1&orientation=portrait',
      subcategories: ['Beds & Frames', 'Nightstands', 'Dressers', 'Bedding'],
      link: '/room-inspiration#bedroom'
    },
    {
      id: 3,
      name: 'Kitchen',
      icon: 'ri-restaurant-line',
      image: 'https://readdy.ai/api/search-image?query=Clean%20minimalist%20kitchen%20interior%20with%20modern%20appliances%2C%20neutral%20tones%2C%20organized%20space%2C%20simple%20elegant%20design%2C%20bright%20lighting%2C%20contemporary%20cabinetry%2C%20functional%20layout&width=400&height=300&seq=kitchen-1&orientation=portrait',
      subcategories: ['Dining Sets', 'Bar Stools', 'Storage', 'Cookware'],
      link: '/room-inspiration#kitchen'
    },
    {
      id: 4,
      name: 'Bathroom',
      icon: 'ri-drop-line',
      image: 'https://readdy.ai/api/search-image?query=Spa-like%20minimalist%20bathroom%20interior%20with%20clean%20fixtures%2C%20neutral%20tones%2C%20organized%20space%2C%20simple%20elegant%20design%2C%20soft%20lighting%2C%20contemporary%20fittings%2C%20serene%20atmosphere&width=400&height=300&seq=bathroom-1&orientation=portrait',
      subcategories: ['Vanities', 'Mirrors', 'Storage', 'Accessories'],
      link: '/room-inspiration#bathroom'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Interior Decor Solutions - Home Decor Reviews & Styling Tips"
        description="Discover handpicked home decor that transforms houses into homes. Honest product reviews, styling tips, and budget-friendly recommendations for homeowners and renters looking to beautify their spaces."
        keywords="home decor, interior design, product reviews, home styling, affordable decor, room makeovers, bathroom decor, home improvement"
        canonical={siteUrl}
        jsonLd={jsonLd}
      />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="px-4 md:px-8 lg:px-20 h-[70px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <h1 className="text-[#1A4D4D] text-xl md:text-2xl lg:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}>
              Interior Decor Solutions
            </h1>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Home</Link>
            <Link to="/blog" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Blog</Link>
            <Link to="/room-inspiration" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Room Inspiration</Link>
            <Link to="/reviews" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Reviews</Link>
            <Link to="/about" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">About</Link>
          </div>

          <button className="hidden lg:flex bg-[#1A4D4D] text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-[#0F3333] transition-all whitespace-nowrap items-center gap-2">
            <i className="ri-mail-line"></i>
            Get Ideas
          </button>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-[#1A4D4D]"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-3">
              <Link to="/" className="block text-[#1A4D4D] font-medium text-base py-2">Home</Link>
              <Link to="/blog" className="block text-[#1A4D4D] font-medium text-base py-2">Blog</Link>
              <Link to="/room-inspiration" className="block text-[#1A4D4D] font-medium text-base py-2">Room Inspiration</Link>
              <Link to="/reviews" className="block text-[#1A4D4D] font-medium text-base py-2">Reviews</Link>
              <Link to="/about" className="block text-[#1A4D4D] font-medium text-base py-2">About</Link>
              <button className="w-full bg-[#1A4D4D] text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-[#0F3333] transition-all whitespace-nowrap flex items-center justify-center gap-2">
                <i className="ri-mail-line"></i>
                Get Ideas
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden mt-[70px]">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=beautiful%20modern%20living%20room%20interior%20with%20natural%20sunlight%20streaming%20through%20large%20windows%20featuring%20comfortable%20neutral%20furniture%20cozy%20textiles%20and%20elegant%20home%20decor%20in%20soft%20teal%20and%20coral%20accent%20colors%20creating%20warm%20inviting%20atmosphere&width=1920&height=1080&seq=hero1&orientation=landscape"
            alt="Beautiful home interior"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-20">
          <div className="absolute top-10 md:top-20 left-4 md:left-8 lg:left-20">
            <span className="inline-block px-3 md:px-4 py-1 md:py-1.5 border border-white/40 rounded-full text-white text-xs md:text-sm font-light">
              Curated for You
            </span>
          </div>

          <div className="absolute bottom-16 md:bottom-24 left-4 md:left-8 lg:left-20 right-4 md:right-auto">
            <h1 className="text-white text-4xl md:text-5xl lg:text-7xl font-light mb-6 md:mb-10 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
              Simple touches.<br />
              Cozy homes.
            </h1>
            <Link 
              to="/reviews"
              className="inline-flex items-center gap-3 md:gap-4 bg-[#1A4D4D] text-white px-6 md:px-8 py-3 md:py-4 rounded-full text-sm md:text-base font-medium hover:bg-[#0F3333] transition-all hover:scale-105 whitespace-nowrap"
            >
              Explore Collections
              <span className="w-7 h-7 md:w-8 md:h-8 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          </div>

          <div className="hidden md:block absolute bottom-32 right-8 lg:right-20 max-w-md">
            <p className="text-white text-lg md:text-xl font-light leading-relaxed">
              Discover handpicked decor that<br />
              transforms houses into homes
            </p>
          </div>
        </div>
      </section>

      {/* Browse by Room Inspiration Section */}
      <section className="py-12 md:py-16 lg:py-20 px-4 md:px-8 lg:px-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 md:mb-16">
            <p className="text-gray-500 text-sm mb-3">(Room by Room)</p>
            <h2 className="text-[#1A4D4D] text-3xl md:text-5xl lg:text-6xl font-bold mb-4">Browse by Room Inspiration</h2>
            <p className="text-gray-600 text-base md:text-lg max-w-2xl">
              Explore curated decor ideas and products for every space in your home
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {roomCategories.map((room) => (
              <div key={room.id} className="bg-[#FAFAFA] rounded-2xl overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer group">
                <div className="w-full h-[200px] md:h-[220px] overflow-hidden">
                  <img 
                    src={room.image}
                    alt={room.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                
                <div className="p-5 md:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#1A4D4D] rounded-lg flex items-center justify-center">
                      <i className={`${room.icon} text-white text-xl`}></i>
                    </div>
                    <h3 className="text-[#1A4D4D] text-xl md:text-2xl font-bold">
                      {room.name}
                    </h3>
                  </div>
                  
                  <ul className="space-y-2 mb-5">
                    {room.subcategories.map((sub, index) => (
                      <li key={index} className="flex items-center gap-2 text-gray-600 text-sm">
                        <i className="ri-arrow-right-s-line text-[#1A4D4D]"></i>
                        <span className="hover:text-[#1A4D4D] transition-colors">{sub}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link 
                    to={room.link}
                    className="block w-full bg-[#1A4D4D] text-white py-2.5 rounded-full font-medium hover:bg-[#0F3333] transition-all whitespace-nowrap text-sm text-center"
                  >
                    Explore {room.name} <i className="ri-arrow-right-line ml-1"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center mt-10 md:mt-12">
            <Link 
              to="/room-inspiration"
              className="bg-[#1A4D4D] text-white px-8 py-3 rounded-full font-medium hover:bg-[#0F3333] transition-all whitespace-nowrap text-sm md:text-base"
            >
              View All Rooms <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-12 md:py-16 lg:py-20 px-4 md:px-8 lg:px-20 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 md:mb-20">
            <div>
              <p className="text-gray-500 text-sm mb-3">(Trending Now)</p>
              <h2 className="text-[#1A4D4D] text-3xl md:text-5xl lg:text-6xl font-bold leading-tight">
                Products We<br />
                Actually Love
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {featuredProducts.map((product) => (
              <Link 
                key={product.id} 
                to={`/reviews/${product.slug}`}
                className="group bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-xl transition-all hover:border-[#FF6B6B] hover:-translate-y-1"
              >
                {/* Product Image */}
                <div className="w-full h-[250px] bg-gray-100 overflow-hidden">
                  <img 
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                {/* Product Content */}
                <div className="p-6">
                  {/* Category Badge */}
                  <span className="inline-block px-3 py-1 bg-[#1A4D4D]/10 text-[#1A4D4D] text-xs font-semibold uppercase rounded-full mb-3">
                    {product.category}
                  </span>

                  {/* Title */}
                  <h3 className="text-[#1A4D4D] text-lg md:text-xl font-bold mb-3 line-clamp-2 group-hover:text-[#FF6B6B] transition-colors">
                    {product.title}
                  </h3>

                  {/* Rating */}
                  <div className="flex items-center gap-2 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i 
                        key={i}
                        className={`${i < product.rating ? 'ri-star-fill text-[#FFB800]' : 'ri-star-line text-gray-300'} text-lg`}
                      ></i>
                    ))}
                    <span className="text-[#1A4D4D] font-bold text-base ml-1">{product.rating}</span>
                  </div>

                  {/* Meta Info */}
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                    <span>{product.author}</span>
                    <span>•</span>
                    <span>{product.date}</span>
                  </div>

                  {/* Excerpt */}
                  <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                    {product.reviewText.substring(0, 150)}...
                  </p>

                  {/* Read More Link */}
                  <div className="flex items-center gap-2 text-[#FF6B6B] font-semibold text-sm group-hover:gap-3 transition-all">
                    <span>Read Full Review</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="hidden md:block mt-8 md:mt-12 text-center">
            <p className="text-gray-500 text-xs md:text-sm italic">
              As an Amazon Associate, we earn from qualifying purchases at no additional cost to you.
            </p>
          </div>
        </div>
      </section>

      {/* Newsletter CTA Banner */}
      <section className="relative py-12 md:py-16 lg:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[#1A4D4D]">
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <pattern id="world-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                <circle cx="50" cy="50" r="2" fill="white" opacity="0.3"/>
                <circle cx="20" cy="30" r="1.5" fill="white" opacity="0.2"/>
                <circle cx="80" cy="70" r="1.5" fill="white" opacity="0.2"/>
              </pattern>
              <rect width="100%" height="100%" fill="url(#world-pattern)"/>
            </svg>
          </div>
        </div>

        <div className="relative z-10 max-w-2xl mx-auto text-center px-4 md:px-8">
          <h2 className="text-white text-3xl md:text-4xl lg:text-5xl font-light mb-3 md:mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Never miss a cozy idea
          </h2>
          <p className="text-white/80 text-base md:text-lg mb-6 md:mb-8">
            Weekly decor tips & exclusive deals
          </p>
          
          <form onSubmit={handleNewsletterSubmit} className="flex items-center justify-center gap-0 max-w-sm mx-auto" data-readdy-form id="newsletter-hero">
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              required
              className="flex-1 h-[50px] md:h-[54px] px-4 md:px-5 bg-transparent border-2 border-white/40 rounded-l-full text-white placeholder-white/60 focus:outline-none focus:border-white/60 text-sm"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-[50px] h-[50px] md:w-[54px] md:h-[54px] bg-[#1A4D4D] rounded-r-full flex items-center justify-center text-white hover:bg-[#0F3333] transition-all disabled:opacity-50 whitespace-nowrap"
            >
              <i className="ri-notification-line text-lg md:text-xl"></i>
            </button>
          </form>
          {submitMessage && (
            <p className={`mt-4 text-sm ${submitMessage.includes('Thank') ? 'text-white' : 'text-[#FF6B6B]'}`}>
              {submitMessage}
            </p>
          )}
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-12 md:py-16 lg:py-20 px-4 md:px-8 lg:px-20 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 md:mb-16">
            <p className="text-gray-500 text-sm mb-3">(Latest Inspiration)</p>
            <h2 className="text-[#1A4D4D] text-3xl md:text-5xl lg:text-6xl font-bold">From Our Blog</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-10">
            {/* Featured Post */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-all h-full flex flex-col md:flex-row cursor-pointer" onClick={() => window.REACT_APP_NAVIGATE(`/blog/${blogPosts[0].id}`)}>
                <div className="w-full md:w-[400px] h-[250px] md:h-auto">
                  <img 
                    src={blogPosts[0].image}
                    alt={blogPosts[0].title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex-1 p-6 md:p-10 flex flex-col justify-between">
                  <div>
                    <span className="inline-block px-3 md:px-4 py-1 md:py-1.5 bg-[#FF6B6B]/10 text-[#FF6B6B] text-xs font-semibold uppercase rounded-full mb-3 md:mb-4">
                      {blogPosts[0].category}
                    </span>
                    <h3 className="text-[#1A4D4D] text-2xl md:text-3xl font-semibold mb-3 line-clamp-3">
                      {blogPosts[0].title}
                    </h3>
                    <p className="text-gray-600 text-sm md:text-base leading-relaxed line-clamp-3 mb-4 md:mb-6">
                      {blogPosts[0].excerpt}
                    </p>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-xs md:text-sm">{blogPosts[0].date}</span>
                    <span className="text-[#FF6B6B] font-medium hover:text-[#FF5252] transition-colors whitespace-nowrap text-sm md:text-base">
                      Read More →
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Smaller Posts */}
            <div className="lg:col-span-2 flex flex-col gap-4 md:gap-5">
              {blogPosts.slice(1).map((post) => (
                <div key={post.id} onClick={() => window.REACT_APP_NAVIGATE(`/blog/${post.id}`)} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all cursor-pointer">
                  <div className="w-full h-[180px] md:h-[200px]">
                    <img 
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-4 md:p-6">
                    <span className="inline-block px-2.5 md:px-3 py-1 bg-[#FF6B6B]/10 text-[#FF6B6B] text-xs font-semibold uppercase rounded-full mb-2 md:mb-3">
                      {post.category}
                    </span>
                    <h3 className="text-[#1A4D4D] text-lg md:text-xl font-semibold mb-2 md:mb-3 line-clamp-2">
                      {post.title}
                    </h3>
                    <span className="text-gray-500 text-xs md:text-sm">{post.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-12 md:py-16 lg:py-20 px-4 md:px-8 lg:px-20 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 md:mb-20">
            <p className="text-gray-500 text-sm mb-4 md:mb-8">(Our Story)</p>
            <div className="flex justify-start lg:justify-end">
              <div className="w-full lg:w-[65%]">
                <h2 className="text-[#1A4D4D] text-3xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 md:mb-10">
                  We believe every<br />
                  home deserves<br />
                  thoughtful touches<br />
                  without the hassle
                </h2>
                <p className="text-gray-600 text-base md:text-xl leading-relaxed max-w-lg">
                  We test, curate, and recommend only products that bring real value to real homes.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-6">
            <div className="md:col-span-2 h-[300px] md:h-[400px] rounded-3xl overflow-hidden shadow-md">
              <img 
                src="https://readdy.ai/api/search-image?query=cozy%20home%20interior%20detail%20shot%20showing%20elegant%20decor%20elements%20including%20soft%20textiles%20natural%20materials%20and%20thoughtful%20styling%20in%20neutral%20tones%20with%20teal%20accents%20creating%20warm%20inviting%20atmosphere&width=600&height=600&seq=about1&orientation=squarish"
                alt="Home detail"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="md:col-span-3 h-[300px] md:h-[400px] rounded-3xl overflow-hidden shadow-md">
              <img 
                src="https://readdy.ai/api/search-image?query=wide%20angle%20view%20of%20beautifully%20styled%20modern%20living%20space%20with%20comfortable%20furniture%20natural%20light%20and%20carefully%20curated%20home%20decor%20in%20neutral%20palette%20with%20coral%20and%20teal%20accents%20showing%20complete%20room%20design&width=900&height=600&seq=about2&orientation=landscape"
                alt="Full room view"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-[#1A4D4D] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <pattern id="home-pattern" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
              <path d="M40 20 L50 30 L50 45 L30 45 L30 30 Z" stroke="white" fill="none" strokeWidth="1"/>
              <circle cx="15" cy="60" r="3" stroke="white" fill="none" strokeWidth="1"/>
              <rect x="60" y="55" width="8" height="8" stroke="white" fill="none" strokeWidth="1"/>
            </pattern>
            <rect width="100%" height="100%" fill="url(#home-pattern)"/>
          </svg>
        </div>

        <div className="relative z-10 px-4 md:px-8 lg:px-20 py-8 md:py-12 lg:py-16">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-10 gap-8 md:gap-10">
            <div className="lg:col-span-3">
              <div className="flex items-center gap-2 mb-6">
                <img src="https://public.readdy.ai/ai/img_res/9e46e3d9-adac-48a0-871c-356cdcae054d.png" alt="Simple Home Decor" className="h-8 w-auto brightness-0 invert" />
              </div>
              
              <form onSubmit={handleNewsletterSubmit} className="flex items-center gap-0 mb-8" data-readdy-form id="newsletter-footer">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  required
                  className="flex-1 h-[50px] px-4 md:px-5 bg-transparent border-2 border-white/40 rounded-l-full text-white placeholder-white/60 focus:outline-none focus:border-white/60 text-sm"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-[50px] h-[50px] bg-[#1A4D4D] rounded-r-full flex items-center justify-center text-white hover:bg-[#0F3333] transition-all disabled:opacity-50 whitespace-nowrap"
                >
                  <i className="ri-notification-line text-lg"></i>
                </button>
              </form>

              <p className="text-white text-2xl md:text-3xl font-light leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
                Simple touches.<br />
                Cozy homes.
              </p>
            </div>

            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Shop</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Living Room</Link></li>
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Bedroom</Link></li>
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Kitchen</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Learn</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/blog" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Blog</Link></li>
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Reviews</Link></li>
                  <li><Link to="/blog" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Guides</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Company</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/about" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">About</Link></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Contact</a></li>
                  <li><Link to="/affiliate-disclosure" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Affiliate Disclosure</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Connect</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Pinterest</a></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Instagram</a></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Newsletter</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 border-t border-white/20 px-4 md:px-8 lg:px-20 py-6 md:py-8">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-white/80 text-xs md:text-sm text-center md:text-left">
              © 2026 Simple Home Decor. All rights reserved.
            </p>

            <div className="flex items-center gap-3 md:gap-4">
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-pinterest-line text-base md:text-lg"></i>
              </a>
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-instagram-line text-base md:text-lg"></i>
              </a>
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-facebook-line text-base md:text-lg"></i>
              </a>
              <a href="https://www.youtube.com/@InteriorDecorSolutions" target="_blank" rel="noopener noreferrer" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-youtube-line text-base md:text-lg"></i>
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4 text-white/80 text-xs md:text-sm">
              <Link to="/privacy" className="hover:text-white transition-colors whitespace-nowrap">Privacy Policy</Link>
              <span className="hidden md:inline">|</span>
              <Link to="/terms" className="hover:text-white transition-colors whitespace-nowrap">Terms of Use</Link>
            </div>
          </div>
          
          <div className="max-w-7xl mx-auto mt-6 pt-6 border-t border-white/10">
            <p className="text-white/60 text-xs text-center leading-relaxed">
              Affiliate Disclosure: This website contains affiliate links. As an Amazon Associate, we earn from qualifying purchases at no additional cost to you.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
